import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Bell, Mail, Smartphone, AlertCircle } from 'lucide-react';

export function NotificationSettings() {
  const [pushNotifications, setPushNotifications] = React.useState(true);
  const [emailNotifications, setEmailNotifications] = React.useState(false);
  const [smsNotifications, setSmsNotifications] = React.useState(false);
  
  // Individual notification types
  const [transactionAlerts, setTransactionAlerts] = React.useState(true);
  const [priceAlerts, setPriceAlerts] = React.useState(true);
  const [stakingRewards, setStakingRewards] = React.useState(true);
  const [securityAlerts, setSecurityAlerts] = React.useState(true);
  const [marketNews, setMarketNews] = React.useState(false);
  
  const [alertFrequency, setAlertFrequency] = React.useState('immediate');

  return (
    <div className="space-y-6">
      {/* Notification Channels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Channels
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Bell className="h-4 w-4 text-blue-600" />
              </div>
              <div className="space-y-0.5">
                <Label>Push Notifications</Label>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  In-app notifications for important events
                </div>
              </div>
            </div>
            <Switch checked={pushNotifications} onCheckedChange={setPushNotifications} />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Mail className="h-4 w-4 text-green-600" />
              </div>
              <div className="space-y-0.5">
                <Label>Email Notifications</Label>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Receive alerts via email
                </div>
              </div>
            </div>
            <Switch checked={emailNotifications} onCheckedChange={setEmailNotifications} />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Smartphone className="h-4 w-4 text-purple-600" />
              </div>
              <div className="space-y-0.5">
                <Label>SMS Notifications</Label>
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Text messages for critical alerts
                </div>
              </div>
            </div>
            <Switch checked={smsNotifications} onCheckedChange={setSmsNotifications} />
          </div>
        </CardContent>
      </Card>

      {/* Notification Types */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Types</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Transaction Alerts</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Notifications for sent, received, and confirmed transactions
              </div>
            </div>
            <Switch checked={transactionAlerts} onCheckedChange={setTransactionAlerts} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Price Alerts</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Alerts when token prices reach your target levels
              </div>
            </div>
            <Switch checked={priceAlerts} onCheckedChange={setPriceAlerts} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Staking Rewards</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Notifications for earned rewards and claiming opportunities
              </div>
            </div>
            <Switch checked={stakingRewards} onCheckedChange={setStakingRewards} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="flex items-center gap-2">
                Security Alerts
                <AlertCircle className="h-4 w-4 text-orange-500" />
              </Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Critical security warnings and suspicious activity
              </div>
            </div>
            <Switch checked={securityAlerts} onCheckedChange={setSecurityAlerts} />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Market News</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Important market updates and DeFi protocol news
              </div>
            </div>
            <Switch checked={marketNews} onCheckedChange={setMarketNews} />
          </div>
        </CardContent>
      </Card>

      {/* Alert Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Alert Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Alert Frequency</Label>
            <Select value={alertFrequency} onValueChange={setAlertFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Immediate</SelectItem>
                <SelectItem value="hourly">Hourly Summary</SelectItem>
                <SelectItem value="daily">Daily Summary</SelectItem>
                <SelectItem value="weekly">Weekly Summary</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              How often to receive non-critical notifications
            </div>
          </div>

          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <h4 className="font-medium mb-3">Price Alert Thresholds</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Minimum Change (%)</Label>
                <Select defaultValue="5">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1%</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="10">10%</SelectItem>
                    <SelectItem value="20">20%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Time Frame</Label>
                <Select defaultValue="24h">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1h">1 Hour</SelectItem>
                    <SelectItem value="4h">4 Hours</SelectItem>
                    <SelectItem value="24h">24 Hours</SelectItem>
                    <SelectItem value="7d">7 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Do Not Disturb */}
      <Card>
        <CardHeader>
          <CardTitle>Do Not Disturb</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Quiet Hours</Label>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Disable non-critical notifications during specified hours
              </div>
            </div>
            <Switch />
          </div>

          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-3">
            <h4 className="font-medium">Quiet Hours Schedule</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">From</Label>
                <Select defaultValue="22:00">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20:00">8:00 PM</SelectItem>
                    <SelectItem value="21:00">9:00 PM</SelectItem>
                    <SelectItem value="22:00">10:00 PM</SelectItem>
                    <SelectItem value="23:00">11:00 PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">To</Label>
                <Select defaultValue="08:00">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="06:00">6:00 AM</SelectItem>
                    <SelectItem value="07:00">7:00 AM</SelectItem>
                    <SelectItem value="08:00">8:00 AM</SelectItem>
                    <SelectItem value="09:00">9:00 AM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Security alerts will still be delivered during quiet hours
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Notifications */}
      <Card>
        <CardHeader>
          <CardTitle>Test Notifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Test your notification settings to ensure they're working correctly.
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Button variant="outline">
              Send Test Push
            </Button>
            <Button variant="outline" disabled={!emailNotifications}>
              Send Test Email
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}